import { useEffect,useState } from 'react';

import { api } from '@/services/api';
import type { SessionContext } from '@/types';

declare global {
  interface Window {
    Bigcommerce?: {
      init: (config: { onLogout?: () => void }) => void;
    };
    __POS_SESSION__?: SessionContext;
  }
}

function getSessionFromHash(): SessionContext | null {
  try {
    const hash = window.location.hash;
    if (!hash) return null;

    const params = new URLSearchParams(hash.slice(1));
    const sessionParam = params.get('session');
    if (!sessionParam) return null;

    const sessionData = JSON.parse(atob(sessionParam)) as SessionContext;

    // Store in sessionStorage for page refreshes
    sessionStorage.setItem('pos_session', JSON.stringify(sessionData));

    // Clean up the URL hash
    window.history.replaceState(null, '', window.location.pathname);

    return sessionData;
  } catch (e) {
    console.error('Failed to parse session from hash:', e);
    return null;
  }
}

function getSessionFromStorage(): SessionContext | null {
  try {
    const stored = sessionStorage.getItem('pos_session');
    if (!stored) return null;
    return JSON.parse(stored) as SessionContext;
  } catch {
    return null;
  }
}

export function useSession() {
  const [session, setSession] = useState<SessionContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Try to get session from URL hash first (initial load from BC)
    let sessionData = getSessionFromHash();

    // Fall back to sessionStorage (page refresh)
    if (!sessionData) {
      sessionData = getSessionFromStorage();
    }

    // Fall back to injected window variable (legacy)
    if (!sessionData && window.__POS_SESSION__) {
      sessionData = window.__POS_SESSION__;
    }

    if (sessionData) {
      // Set API session immediately before any components can fetch
      api.setSession(sessionData.sessionToken, sessionData.storeHash);

      setSession(sessionData);
      setIsLoading(false);

      // Initialize BigCommerce SDK for session sync
      if (window.Bigcommerce) {
        window.Bigcommerce.init({
          onLogout: () => {
            if (sessionData?.sessionToken) {
              api.revokeSession(sessionData.sessionToken).catch((err) => {
                console.warn('[Admin] Failed to revoke session token:', err);
              });
            }
            setSession(null);
            sessionStorage.removeItem('pos_session');
            setError('Session ended. Please reload the page.');
          },
        });
      }
    } else {
      setError('Session data not found. Please reload the app from BigCommerce.');
      setIsLoading(false);
    }
  }, []);

  return { session, isLoading, error };
}
